import useApi from '@/lib/api/useApi';
import { useContext, useState } from 'react';
import Dialog from '@/components/Dialog';
import { FileStatus, type UploadedImage } from '@/lib/types';
import UploadedImagesContext from '../../UploadedImagesContext';
import FormattedHtmlMessage from '@/components/FormattedHtmlMessage';
import { FormattedMessage } from 'react-intl';
import ErrorModeDeletionWarning from './ErrorModeDeletionWarning';
import FileChipRemoveButton from '../../FileChip/FileChipRemoveButton';

type DeleteUploadedImageProps = {
  image: UploadedImage;
  useErrorMode: boolean;
};

const DeleteUploadedImage = ({
  image,
  useErrorMode,
}: DeleteUploadedImageProps) => {
  const api = useApi();

  const { imagesEndpoint, removeImageFromLocalState } = useContext(
    UploadedImagesContext,
  );

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteImage = async (): Promise<void> => {
    setDeleting(true);

    setConfirmationDialogOpen(false);

    await api.delete(`${imagesEndpoint}/${image.id}`);

    removeImageFromLocalState(image);

    setDeleting(false);
  };

  return (
    <>
      <FileChipRemoveButton
        onClick={() => setConfirmationDialogOpen(true)}
        disabled={deleting}
        useErrorMode={useErrorMode}
      />
      <Dialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        dangerous
      >
        <Dialog.Title>
          <FormattedMessage id="portfolio.challenge.files.uploaded_image.delete.confirmation_dialog.title" />
        </Dialog.Title>
        <Dialog.Content>
          <Dialog.ContentText>
            <FormattedHtmlMessage
              id="portfolio.challenge.files.uploaded_image.delete.confirmation_dialog.text"
              values={{
                imageFilename: image.file.originalFilename,
              }}
            />
          </Dialog.ContentText>
          {useErrorMode && (
            <ErrorModeDeletionWarning severity="warning">
              <FormattedHtmlMessage
                id={`portfolio.challenge.files.uploaded_image.delete.confirmation_dialog.error_state_messages.${image.file.status === FileStatus.ANTIVIRUS_QUARANTINED ? 'malware' : 'inappropriate_content'}`}
              />
            </ErrorModeDeletionWarning>
          )}
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.Actions.CancelButton
            onClick={() => setConfirmationDialogOpen(false)}
          />
          <Dialog.Actions.DeleteButton onClick={handleDeleteImage} />
        </Dialog.Actions>
      </Dialog>
    </>
  );
};

export default DeleteUploadedImage;
